import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import { Card, Row, Col, Button, Form, Modal } from 'react-bootstrap';
import { DataTable, CardContainer, Heading5 } from '../widgets';
import BoardRow from './BoardRow';
import "../../../assets/scss/board.scss";
import { SubNav } from '../layout/submenu';
import { getUserFarmList } from '../../actions/farm';
import { bulkAssignFarm, getBoardList } from '../../actions/hardware';
import { DataTableLoading, DataTableRecordsNotFound } from '../widgets/DataTable';
import { BoardProvider, useBoardContext } from './context/BoardContext';
import { searchWord, usePermission } from '../../helpers/common';


const BoardList = () => {
    const history = useHistory();
    const { selectedHardware, setSelectedHardware, boardList, handleBulkSelect } = useBoardContext();

    const [showModal, setShowModal] = useState(false);
    const [assignFarmModal, setAssignFarmModal] = useState(false);
    const [selectedFarm, setSelectedFarm] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [searchText, setSearchText] = useState("");

    const searchInput = React.createRef();
    const allow = usePermission();
    const isAllowEdit = allow("board-console", "edit");

    const dispatch = useDispatch();
    const farmId = useSelector(state => state.farm.currentFarm.farm_uuid);
    const farmList = useSelector(state => state.farm.userFarmList);
    const isRequesting = useSelector(state => state.hardware.boardList.isRequesting);
    let index = 0;
    useEffect(() => {
        dispatch(getUserFarmList());
        dispatch(getBoardList());
    }, []);

    const farmListOptions = farmList.map(item => ({
        value: item.farm_uuid,
        label: item.farm_name
    }));

    const getFilteredBoardList = () => {
        let list = boardList;
        const isSearchTextMatched = value => searchWord(value, searchText);
        list = list.filter(e => {
            if (searchText.trim().length > 0) {
                return isSearchTextMatched(e.hardware_name) ||
                    e.farm_list.some(item => isSearchTextMatched(item.farm_name)) ||
                    isSearchTextMatched(e.current_hardware_info.equipment_group_name) ||
                    isSearchTextMatched(e.current_hardware_info.can_code) ||
                    isSearchTextMatched(e.current_hardware_info.imei);
            } else return true;
        });
        return list;
    }

    const handleBoardList = (hardware_uuid, e) => {
        if (e.target.checked && !selectedHardware.includes(hardware_uuid)) setSelectedHardware([...selectedHardware, hardware_uuid]);
        else if (!e.target.checked && selectedHardware.includes(hardware_uuid)) {
            const selectedHardwareList = selectedHardware.filter(e => e !== hardware_uuid);
            setSelectedHardware(selectedHardwareList);
        }
    }

    const handleAssignFarm = () => {
        const data = {
            farm_uuid: selectedFarm,
            hardware_uuid: selectedHardware
        }
        const cb = () => {
            dispatch(getBoardList())
            setIsUpdating(false);
        };
        setIsUpdating(true);
        dispatch(bulkAssignFarm(data, cb));
        setSelectedHardware([]);
    }

    const filteredBoardList = useMemo(getFilteredBoardList, [boardList, searchText]);

    return <>
        <CardContainer lg={10} md={10}>
            <Card className="card-list animate animate__fadeInUp">
                <Card.Header>
                    <Row>
                        <Col>
                            <Card.Title>
                                <Heading5>Board Console</Heading5>
                            </Card.Title>
                        </Col>
                        <Col>
                            <Button
                                className='btn btn-sm float-right'
                                variant='danger'
                                onClick={() => {
                                    const url = `/home/farm/${farmId}/map`;
                                    history.push(url);
                                }}>
                                <i className='fa fa-times-circle mr-1' />
                                Cancel
                            </Button>

                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {isAllowEdit && <Row className="farm-assign">
                        {<div className="m-0 search-container">
                            <SubNav.SearchBox
                                ref={searchInput}
                                size="md"
                                placeholder="Search for device name, farm, installed in, IMEI"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                onClear={() => { setSearchText(""); }}
                                className="input"
                            />
                        </div>}
                        {<Form.Select
                            id="boardconsole"
                            placeholder="Select Farm"
                            name="boardConsole"
                            className="farm-select"
                            onChange={(e) => {
                                const value = e ? e.value : null
                                setSelectedFarm(value)
                            }}
                            value={selectedFarm}
                            options={farmListOptions}
                            sortType="asc" />}
                        {<Button
                            style={{
                                cursor: `${isUpdating || (selectedHardware.length === 0 || !selectedFarm)
                                    ? 'not-allowed' : 'pointer'} `
                            }}
                            variant="primary"
                            size="sm"
                            onClick={() => setAssignFarmModal(!assignFarmModal)}
                            disabled={selectedHardware.length === 0 || !selectedFarm}
                        >
                            {isUpdating ? 'Updating...' : 'Assign Farm'}
                        </Button>}
                    </Row>}
                    <Row>
                        <Col>
                            <DataTable>
                                <DataTable.Header>
                                    <DataTable.Row>
                                        <DataTable.Cell style={{ width: "60px" }}>#</DataTable.Cell>
                                        <DataTable.Cell style={{ width: "160px" }}>Device Name</DataTable.Cell>
                                        <DataTable.Cell >Farms</DataTable.Cell>
                                        <DataTable.Cell style={{ width: "240px" }}>Installed in</DataTable.Cell>
                                        <DataTable.Cell style={{ width: "280px" }}>Serial Number</DataTable.Cell>
                                        <DataTable.Cell style={{ width: "120px" }}>Action</DataTable.Cell>
                                        {isAllowEdit && <DataTable.Cell width="45px">
                                            <SubNav.Checkbox
                                                checked={!isRequesting && selectedHardware.length === boardList.length}
                                                className={`square`}
                                                onChange={(e) => handleBulkSelect(e.target.checked)}
                                            />
                                        </DataTable.Cell>}
                                    </DataTable.Row>
                                </DataTable.Header>
                                <DataTable.Body>
                                    {filteredBoardList.map((board, i) => {
                                        if (board) index += 1;
                                        return (
                                            <BoardRow
                                                key={i}
                                                setShowModal={() => setShowModal(!showModal)}
                                                board={board}
                                                index={index}
                                                handleBoardList={handleBoardList}
                                                selectedHardware={selectedHardware}
                                                isAllowEdit={isAllowEdit} />
                                        )
                                    })
                                    }
                                </DataTable.Body>
                            </DataTable>
                            {isRequesting && <DataTableLoading />}
                            {(!isRequesting && filteredBoardList.length === 0) && <DataTableRecordsNotFound />}
                            {assignFarmModal && <Modal.Confirm
                                className="modal-bordered"
                                show={assignFarmModal}
                                onConfirm={() => handleAssignFarm()}
                                onHide={() => setAssignFarmModal(!assignFarmModal)}
                                iconClassName="fa fa-question"
                                variant="info"
                                confirmButton={{ variant: "info" }}
                                title={{ content: "Confirmation" }}
                                content={`Are you sure to assign farm?`}
                            />}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </CardContainer>
    </>
}

export default ((props) => <BoardProvider><BoardList {...props} /></BoardProvider>);
